"use client";

import { useCallback, useEffect, useState } from "react";
import useAdminApiHook from "../../../../utils/useAdminApi";
import type { RoleApiResponse } from "../../../../interface/interfaces";
import DataTable, { type TableColumn } from "react-data-table-component";
import Loading from "../../../../merchant_dashboard/components/loading/Loading";
import { customStyles_T } from "../../../../utils/table_utils";
import type { Roles } from "../../../../interface/interfaces";
import RoleActions from "./RoleActions";
import RoleDetailsModal from "./RoleDetailsModal";
import UpdateRoleModal from "./UpdateRole";

const RolesTable = () => {
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(5);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [selectedRole, setSelectedRole] = useState<Roles | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [updateRole, setUpdateRole] = useState<Roles | null>(null);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);

  const { data, isLoading, makeRequest } = useAdminApiHook<RoleApiResponse>(
    "/admin/roles",
    {
      method: "get",
    }
  );

  const loadRoles = useCallback(async () => {
    const params = {
      page,
      size: perPage,
    };

    const response = await makeRequest(params);

    if (response?.data?.statusCode !== "00") {
      setTotalRows(0);
    } else if (response?.data?.data) {
      setTotalRows(response.data.data.totalCount || 0);
    }
  }, [page, perPage]);

  useEffect(() => {
    loadRoles();
  }, [page, perPage]);

  const handlePageChange = (page: number) => {
    setPage(page);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setPage(page);
  };

  const openRoleModal = (role: Roles) => {
    setSelectedRole(role);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRole(null);
  };

  const openUpdateModal = (role: Roles) => {
    setUpdateRole(role);
    setIsUpdateModalOpen(true);
  };

  const closeUpdateModal = () => {
    setIsUpdateModalOpen(false);
    setUpdateRole(null);
  };

  const StatusBadge = ({ status }: { status: string }) => {
    const statusColors = {
      ACTIVE: "bg-green-100 text-green-800 border-green-300",
      INACTIVE: "bg-red-100 text-red-800 border-red-300",
      PENDING: "bg-yellow-100 text-yellow-800 border-yellow-300",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium border ${
          statusColors[status as keyof typeof statusColors] ||
          "bg-gray-100 text-gray-800 border-gray-300"
        }`}
      >
        {status}
      </span>
    );
  };

  const RoleTypesBadges = ({ role }: { role: Roles }) => {
    const types = [];
    if (role.isAdminRole)
      types.push({
        label: "Admin",
        color: "bg-blue-100 text-blue-800 border-blue-300",
      });
    if (role.isSystemRole)
      types.push({
        label: "System",
        color: "bg-purple-100 text-purple-800 border-purple-300",
      });
    if (role.isCustomRole)
      types.push({
        label: "Custom",
        color: "bg-green-100 text-green-800 border-green-300",
      });
    if (role.isMerchantRole)
      types.push({
        label: "Merchant",
        color: "bg-orange-100 text-orange-800 border-orange-300",
      });

    return (
      <div className="flex flex-wrap gap-1">
        {types.map((type, index) => (
          <span
            key={index}
            className={`px-2 py-1 rounded text-xs font-medium border ${type.color}`}
          >
            {type.label}
          </span>
        ))}
        {types.length === 0 && (
          <span className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 border border-gray-300">
            None
          </span>
        )}
      </div>
    );
  };

  const PermissionsSummary = ({ role }: { role: Roles }) => {
    const rolePermCount = role.rolePermissions?.length || 0;
    const adminPermCount = role.adminRolePermissions?.length || 0;
    const totalPerms = rolePermCount + adminPermCount;

    return (
      <div className="text-sm">
        <div className="font-medium text-gray-900">{totalPerms} total</div>
        <div className="text-xs text-gray-500">
          {rolePermCount} role • {adminPermCount} admin
        </div>
      </div>
    );
  };

  const columns: TableColumn<Roles>[] = [
    {
      name: "Role Name",
      selector: (row) => row.roleName,
      sortable: true,
      cell: (row) => (
        <div>
          <div className="font-medium text-gray-900">{row.roleName}</div>
        </div>
      ),
      minWidth: "200px",
      grow: 1,
    },
    {
      name: "Role Types",
      selector: (row) =>
        `${row.isAdminRole}${row.isSystemRole}${row.isCustomRole}${row.isMerchantRole}`,
      sortable: false,
      cell: (row) => <RoleTypesBadges role={row} />,
      minWidth: "200px",
    },
    {
      name: "Status",
      selector: (row) => row.roleStatus,
      sortable: true,
      cell: (row) => <StatusBadge status={row.roleStatus} />,
      minWidth: "100px",
    },
    {
      name: "Permissions",
      selector: (row) =>
        (row.rolePermissions?.length || 0) +
        (row.adminRolePermissions?.length || 0),
      sortable: true,
      cell: (row) => <PermissionsSummary role={row} />,
    },
    {
      name: "Actions",
      sortable: false,
      cell: (row) => (
        <RoleActions
          role={row}
          onViewDetails={openRoleModal}
          onUpdate={openUpdateModal}
        />
      ),
      minWidth: "400px",
      right: true,
    },
  ];

  return (
    <section>
      <main>
        <DataTable
          columns={columns}
          data={data?.data?.data || []}
          progressPending={isLoading}
          progressComponent={<Loading />}
          pagination
          paginationServer
          paginationTotalRows={totalRows}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handlePerRowsChange}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[10, 20, 30, 50]}
          highlightOnHover
          responsive
          persistTableHead
          customStyles={customStyles_T}
        />
        <RoleDetailsModal
          selectedRole={selectedRole}
          isModalOpen={isModalOpen}
          onClose={closeModal}
        />
        <UpdateRoleModal
          role={updateRole}
          isOpen={isUpdateModalOpen}
          onClose={closeUpdateModal}
        />
      </main>
    </section>
  );
};

export default RolesTable;
